import type { SiteTopology } from "../types";

/**
 * BESS Site 2 — synthetic demo site (no real-world counterpart).
 *
 * A smaller commercial profile: 1× BESS + on-site PV behind a single grid
 * meter. Gives the simulator / TB demo page a second site with a different
 * shape (PV self-consumption, single cabinet) for the site switcher.
 */
export const bessSite2: SiteTopology = {
  id: "bess-site-2",
  name: "BESS Site 2",
  shortLabel: "Site 2",
  timezone: "Europe/Rome",
  aggregation: {
    grid_W: "smartmeter/aggregation/grid_W",
    battery_power_W: "battery/aggregation/power",
    battery_soc_avg: "battery/aggregation/SOC",
    pv_power_W: "pv/measurement/1/power_W",
  },
  bess: [
    {
      id: "bess-1",
      name: "BESS-1",
      family: "monet",
      protocol: "modbus-tcp",
      host: "192.168.18.102",
      port: 502,
      capacity_kWh: 215,
      max_charge_kW: 110,
      max_discharge_kW: 110,
      invert: {
        active_power_measurement: true,
        active_power_setpoint: true,
        reactive_power_measurement: false,
        reactive_power_setpoint: false,
      },
      topics: {
        soc: "battery/measurement/1/SOC",
        ac_active_power: "battery/measurement/1/ac_active_power",
        ac_reactive_power: "battery/measurement/1/ac_reactive_power",
        total_voltage: "battery/measurement/1/Total_Voltage",
        ambient_temperature: "battery/measurement/1/ambient_temperature",
        accumulated_charge: "battery/measurement/1/accumulated/positive",
        accumulated_discharge: "battery/measurement/1/accumulated/negative",
        fault_flags_prefix: "battery/measurement/1/fault_",
        setpoint_active: "controller/1/output/power_W",
        setpoint_reactive: "controller/1/output/power_VAr",
        bess_onoff: "controller/1/output/BESS_ONOFF",
        dc_relays: "controller/1/output/DCRelays",
      },
      operating_profile: {
        watchdog_action: "zero_then_off",
        setpoint_timeout_s: 20,
        slew_fast_pu_s: 10,
        slew_slow_pu_s: 2,
        slew_threshold_kW: 5,
        interrogation_period_s: 60,
      },
    },
  ],
  smartmeters: [
    {
      id: "sm-grid",
      name: "Grid coupling",
      role: "grid",
      protocol: "modbus-tcp",
      host: "192.168.18.101",
      port: 502,
      factor: -1,
      topics: {
        power_W: "smartmeter/measurement/1/power_W",
        power_VAr: "smartmeter/measurement/1/power_VAr",
        voltage_V_L1: "smartmeter/measurement/1/voltage_V_L1",
        voltage_V_L2: "smartmeter/measurement/1/voltage_V_L2",
        voltage_V_L3: "smartmeter/measurement/1/voltage_V_L3",
        current_A: "smartmeter/measurement/1/current_A",
        frequency_Hz: "smartmeter/measurement/1/frequency_Hz",
      },
    },
    {
      id: "sm-pv",
      name: "PV inverter feed",
      role: "pv",
      protocol: "modbus-tcp",
      host: "192.168.18.103",
      port: 502,
      factor: 1,
      topics: { power_W: "pv/measurement/1/power_W" },
    },
  ],
  controller: {
    id: "controller-1",
    grid_smartmeter_id: "sm-grid",
    modes: ["gridZeroControl", "PeakShaving", "ConstantPower", "OFF"],
    schedule_attribute: "bess_schedule",
    diagnostic_topic: "controller/diagnostics",
  },
  prices: {
    zone: "IT-NORD",
    currency: "EUR",
    day_ahead_attribute: "next_day_prices",
    carbon_intensity_g_kWh: 245,
  },
  scadaPreset: "compact",
  // Synthetic demo site — the LV-bus claim is part of the fiction and
  // safe to assert here (real-site topologies omit this unless known).
  bus_label: "SITE BUS · 400 V · 50 Hz",
};
